/**
 * PasswordField — Iter-108.3 · Platform-wide password input.
 *
 * A single reusable password input carrying all four enhancements the
 * CEO requested for the registration flow:
 *   • Eye/EyeOff visibility toggle aligned inside the input
 *   • Live strength meter (Weak / Fair / Strong / Excellent) — opt-in
 *     via `showStrength` for "new password" fields (skip on login)
 *   • Green ✓ MATCH indicator — enabled by passing `matchValue`
 *   • Paste guard flag — call `onPasteWarning(true)` from the parent to
 *     display a warning strip when the pasted clipboard content looks
 *     like an email or company name
 *
 * Usage:
 *   <PasswordField
 *     label="Password"
 *     required
 *     value={pwd}
 *     onChange={setPwd}
 *     showStrength
 *     onPasteWarning={setPasted}
 *     testId="login-password"
 *   />
 */
import { useMemo, useState } from 'react';
import { Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { Input } from './ui/input';

/** Bucket a password into { score 0-4, label, tone hex } for the meter. */
export function scorePasswordStrength(pwd) {
  if (!pwd) return { score: 0, label: '', tone: '#3f3f46' };
  let score = 0;
  if (pwd.length >= 8) score += 1;
  if (pwd.length >= 12) score += 1;
  if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score += 1;
  if (/\d/.test(pwd)) score += 0.5;
  if (/[^A-Za-z0-9]/.test(pwd)) score += 0.5;
  const bucket = Math.min(4, Math.max(1, Math.round(score)));
  const map = {
    1: { label: 'Weak',      tone: '#ef4444' },
    2: { label: 'Fair',      tone: '#f59e0b' },
    3: { label: 'Strong',    tone: '#22c55e' },
    4: { label: 'Excellent', tone: '#10b981' },
  };
  return { score: bucket, ...map[bucket] };
}

/** True when clipboard content looks like an email / company name. */
export function looksLikeIdentity(text) {
  const t = (text || '').trim();
  if (!t) return false;
  return (
    /@/.test(t) ||
    /\s(inc|llc|corp|company|group|properties)\b/i.test(t) ||
    /\bnoble empire\b/i.test(t)
  );
}

export default function PasswordField({
  label = 'Password',
  hint,
  required = false,
  minLength = 8,
  value,
  onChange,
  reveal,               // controlled visibility (optional, from "reveal both")
  onToggleReveal,       // controlled visibility toggle
  showStrength = false, // meter on/off
  matchValue,           // if provided, renders MATCH/NO MATCH indicator
  onPasteWarning,       // (bool) => void — parent shows warning strip
  testId = 'password',
  className = '',
  autoComplete = 'current-password',
  disabled = false,
}) {
  // Local visibility fallback when parent isn't controlling it.
  const [localShow, setLocalShow] = useLocalReveal();
  const isControlled = typeof reveal === 'boolean';
  const show = isControlled ? reveal : localShow;
  const toggle = isControlled ? onToggleReveal : () => setLocalShow((v) => !v);

  const strength = useMemo(
    () => (showStrength ? scorePasswordStrength(value || '') : null),
    [value, showStrength]
  );
  const showMatch = typeof matchValue === 'string' && matchValue.length > 0 && (value || '').length > 0;
  const matched = showMatch && matchValue === value;

  const handlePaste = (e) => {
    try {
      const txt = e.clipboardData?.getData('text') || '';
      if (looksLikeIdentity(txt) && typeof onPasteWarning === 'function') {
        onPasteWarning(true);
        setTimeout(() => onPasteWarning(false), 6000);
      }
    } catch { /* silent */ }
  };

  return (
    <div className={className} data-testid={`${testId}-field`}>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">
          {required && <span className="text-[#C9A962] mr-1">*</span>}
          {label}
          {hint && <span className="ml-1 normal-case tracking-normal text-zinc-500">{hint}</span>}
        </span>
        {showStrength && value && (
          <span
            className="text-[9px] uppercase tracking-widest font-bold"
            style={{ color: strength.tone }}
            data-testid={`${testId}-strength-label`}
          >
            {strength.label}
          </span>
        )}
        {showMatch && (
          matched ? (
            <span
              className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold"
              style={{ color: '#22c55e' }}
              data-testid={`${testId}-match-ok`}
            >
              <CheckCircle2 className="h-3 w-3" /> Match
            </span>
          ) : (
            <span
              className="text-[9px] uppercase tracking-widest font-bold"
              style={{ color: '#ef4444' }}
              data-testid={`${testId}-match-nomatch`}
            >
              No match
            </span>
          )
        )}
      </div>
      <div className="relative">
        <Input
          required={required}
          type={show ? 'text' : 'password'}
          minLength={minLength}
          value={value || ''}
          onChange={(e) => onChange && onChange(e.target.value)}
          onPaste={handlePaste}
          disabled={disabled}
          autoComplete={autoComplete}
          className="bg-zinc-800 border-zinc-700 text-white pr-10"
          data-testid={testId}
        />
        <button
          type="button"
          onClick={toggle}
          className="absolute inset-y-0 right-2 flex items-center text-zinc-400 hover:text-[#C9A962]"
          tabIndex={-1}
          aria-label={show ? 'Hide password' : 'Show password'}
          data-testid={`${testId}-eye`}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {showStrength && (
        <div
          className="mt-1.5 grid grid-cols-4 gap-1"
          data-testid={`${testId}-strength-meter`}
          data-score={strength.score}
        >
          {[1, 2, 3, 4].map((tick) => (
            <span
              key={tick}
              className="h-1 rounded-full transition"
              style={{
                backgroundColor: strength.score >= tick ? strength.tone : '#3f3f46',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* Local reveal state helper. */
function useLocalReveal() {
  return useState(false);
}
